import { ArrayHelpers, AugmentedModelObject } from "@tylertech/metadata-module";
import { Utilities, } from "@tylertech/shared-module";
import { CombinedViewMetadata, IViewMetadata, RepositoryDataCollection, ViewMetadata } from "@tylertech/tyler-api-types";
import { MetadataErrorService, MetadataService, MetadataViewService } from "../../../services";
import { Observable } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { ModelChangeArg } from "../../../interfaces";

export class Tree
{
    constructor(
        readonly name: string,
        readonly nodes: Array<TreeNode> = [],
        public expanded?: boolean)
    {
        this.id = Utilities.generateRandomGuid();
    }

    id: string;

    select(node: Leaf): void
    {
        for (const child of this.nodes)
        {
            if (!Leaf.isLeaf(child))
            {
                child.select(node);
            }
            else
            {
                child.selected = child === node;
            }
        }
    }

    getLeafById(id: string): Leaf
    {
        for (const child of this.nodes)
        {
            if (!Leaf.isLeaf(child))
            {
                const foundChild = child.getLeafById(id);
                if (foundChild)
                    return foundChild;
            }
            else
            {
                if (child.id === id)
                    return child;
            }
        }
        return null;
    }

}

export class Leaf
{
    constructor(
        public readonly view: IViewMetadata,
        public readonly model: Readonly<AugmentedModelObject>,
        public readonly datacontroller: Readonly<RepositoryDataCollection>,
        private readonly $metadataService: MetadataService,
        private readonly $metadataViewService: MetadataViewService,
        private readonly $metadataErrorService: MetadataErrorService,
        private readonly destroy$: Observable<void>
    )
    {
        this.id = Utilities.generateRandomGuid();
        this.name = this.computeName();
        this.disabled = this.computeDisabled();
        this.hasError = this.computeHasError();

        this.$metadataService.onProxyPropertyChanged.pipe(takeUntil(this.destroy$), filter((arg: ModelChangeArg) => this.$metadataService.compareModels(arg.Model, this.model)))
            .subscribe(() =>
            {
                this.name = this.computeName();
                this.disabled = this.computeDisabled();
            });
    }

    id: string;
    name: string;
    disabled: boolean;
    hasError: boolean;

    private _selected: boolean;

    get selected(): boolean
    {
        return this._selected;
    }

    set selected(value: boolean)
    {
        this._selected = value;
    }

    computeName(): string
    {
        const title = this.$metadataViewService.GetTitle(this.view, this.model, this.datacontroller);
        if (this.view.CountProperty)
        {
            let model = this.model;
            if (this.view.SubPropertyType && this.datacontroller.OtherEntities[this.view.SubPropertyType])
                model = this.datacontroller.OtherEntities[this.view.SubPropertyType][0];
            const viewCount = this.$metadataService.DependentModuleValue(this.view.CountProperty, this.model["$$Parent"], model);
            if (viewCount)
                return `${title} (${viewCount})`;
        }
        return title;
    }

    computeDisabled(): boolean
    {
        if (!this.view.DisabledProperty)
            return false;
        return this.$metadataViewService.IsDisabled(this.view, this.model, this.datacontroller);
    }

    computeHasError(): boolean
    {
        const self = this;
        const view = self.view;
        return checkAllViewsForErrors(view);

        function checkAllViewsForErrors(view: ViewMetadata): boolean
        {
            if (self.$metadataViewService.IsGrid(view))
                //do not run this errorcheck if it's a gridview. It won't work as-is.
                return false;
            else if (self.$metadataViewService.IsCombinedView(view))
            {
                const combinedView = view as CombinedViewMetadata;
                return combinedView.Views.some(partialView =>
                {
                    return checkAllViewsForErrors(partialView);
                });
            }
            else
            {
                const viewType = self.$metadataViewService.GetTypeName(view);
                if (viewType === self.datacontroller.Type && self.$metadataErrorService.viewHasErrors(view, self.model))
                    return true;
                else if (viewType in self.datacontroller.OtherEntities)
                {
                    const others = self.datacontroller.OtherEntities[viewType];
                    if (ArrayHelpers.any(others, x => self.$metadataErrorService.viewHasErrors(view, x)))
                        return true;
                }
            }
            return false;
        }
    }

    static isLeaf(node: TreeNode): node is Leaf
    {
        return node instanceof Leaf;
    }
}

export interface OnContextEvent
{
    Id: string;
    Event: MouseEvent;
}

export type TreeNode = Tree | Leaf;
